import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Activity, Loader2, RefreshCw, Users, LogIn, LogOut, Clock, Radio,
  MousePointerClick, Monitor, Globe, History, ListTree, Eye, UserCircle2,
} from 'lucide-react';

const WINDOW_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtDateTime(iso) {
  return iso ? new Date(iso).toLocaleString() : '—';
}

const ROLE_COLOR = {
  super_admin: 'text-rose-400 border-rose-400/40',
  admin: 'text-amber-400 border-amber-400/40',
  tenant_admin: 'text-amber-400 border-amber-400/40',
  manager: 'text-sky-400 border-sky-400/40',
  analyst: 'text-emerald-400 border-emerald-400/40',
  viewer: 'text-muted-foreground border-border',
};

function RoleBadge({ role }) {
  return (
    <Badge variant="outline" className={`text-[9px] capitalize ${ROLE_COLOR[role] || 'text-muted-foreground'}`}>
      {(role || 'viewer').replace('_', ' ')}
    </Badge>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, accent = 'text-primary', testid }) {
  return (
    <Card className="p-4 flex items-start gap-3" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg bg-muted/40 flex items-center justify-center shrink-0 ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-heading font-bold text-foreground leading-tight">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {sub && <div className="text-[11px] text-muted-foreground/70 mt-0.5">{sub}</div>}
      </div>
    </Card>
  );
}

// ─── Activity timeline dialog ────────────────────────────────────────────────
function TimelineDialog({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.getUserActivityTimeline(userId, 150)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <Dialog open={!!userId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="activity-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTree className="w-4 h-4 text-primary" />
            Activity — {data?.user_name || 'User'}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading activity…
          </div>
        ) : !data || data.events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2" data-testid="activity-timeline-empty">
            <History className="w-7 h-7 opacity-40" />
            <p className="text-sm">No recorded activity yet.</p>
          </div>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-1" data-testid="activity-timeline-list">
            {data.events.map((e, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-muted/30 text-sm" data-testid={`timeline-event-${i}`}>
                <div className="w-7 h-7 rounded-md bg-muted/40 flex items-center justify-center shrink-0 mt-0.5">
                  <MousePointerClick className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-foreground truncate">{e.description || e.type}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDateTime(e.timestamp)}</div>
                </div>
                {e.object_type && <Badge variant="outline" className="text-[9px] capitalize shrink-0">{e.object_type}</Badge>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Live tab ─────────────────────────────────────────────────────────────
function LiveTab({ onPick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.getActivityLive();
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // auto-refresh every 20s
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading live sessions…</div>;
  }

  const users = data?.users || [];
  return (
    <div className="space-y-4" data-testid="activity-live-tab">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <span className="text-foreground font-semibold" data-testid="live-online-count">{data?.online_count ?? 0}</span>
        online now · active within {data?.window_minutes ?? 5} min · auto-refreshes
      </div>
      <Card className="p-0 overflow-hidden">
        {users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2" data-testid="activity-live-empty">
            <Radio className="w-8 h-8 opacity-40" />
            <p className="text-sm">No users are currently online.</p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-muted-foreground/70 font-semibold bg-muted/30">
              <div className="col-span-3">User</div>
              <div className="col-span-3">Currently viewing</div>
              <div className="col-span-2">Online for</div>
              <div className="col-span-2">Last seen</div>
              <div className="col-span-2 text-right">Activity</div>
            </div>
            {users.map((u, i) => (
              <div key={u.user_id || i} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm hover:bg-muted/20" data-testid={`live-row-${i}`}>
                <div className="col-span-3 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    <span className="font-medium text-foreground truncate">{u.user_name || u.user_email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 pl-4">
                    <RoleBadge role={u.role} />
                    <span className="text-[10px] text-muted-foreground truncate">{u.tenant_name}</span>
                  </div>
                </div>
                <div className="col-span-3 min-w-0 flex items-center gap-1.5">
                  <Monitor className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate text-foreground">{u.current_label || '—'}</span>
                </div>
                <div className="col-span-2 text-muted-foreground">{u.duration_label}</div>
                <div className="col-span-2 text-muted-foreground text-xs">{timeAgo(u.last_seen_at)}</div>
                <div className="col-span-2 text-right">
                  <Button variant="outline" size="sm" className="h-7 rounded-lg gap-1 text-xs" onClick={() => onPick(u.user_id)} data-testid={`live-view-activity-${i}`}>
                    <Eye className="w-3 h-3" /> View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Statistics tab ───────────────────────────────────────────────────────
function StatsTab({ days, onPick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getActivityStats(days).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Crunching statistics…</div>;
  }
  if (!data) return <div className="py-16 text-center text-muted-foreground">Could not load statistics.</div>;

  const maxDay = Math.max(1, ...(data.logins_by_day || []).map((d) => d.logins));

  return (
    <div className="space-y-5" data-testid="activity-stats-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Radio} label="Active now" value={data.active_now} accent="text-emerald-400" testid="stat-active-now" />
        <StatCard icon={LogIn} label="Logins" value={data.logins} accent="text-sky-400" testid="stat-logins" />
        <StatCard icon={LogOut} label="Logouts" value={data.logouts} accent="text-orange-400" testid="stat-logouts" />
        <StatCard icon={Users} label="Unique users" value={data.unique_users} accent="text-violet-400" testid="stat-unique-users" />
        <StatCard icon={Clock} label="Avg. session" value={data.avg_session_label} accent="text-amber-400" testid="stat-avg-session" />
        <StatCard icon={Activity} label="Time on system" value={data.total_time_label} accent="text-primary" testid="stat-total-time" />
      </div>

      <Card className="p-4" data-testid="logins-by-day-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Logins per day</h3>
          <span className="text-xs text-muted-foreground">last {data.window_days} days</span>
        </div>
        <div className="flex items-end gap-[3px] h-32">
          {(data.logins_by_day || []).map((d, i) => (
            <div key={d.date} className="flex-1 group relative flex flex-col justify-end items-center" data-testid={`logins-bar-${i}`}>
              <div
                className="w-full rounded-t bg-primary/70 hover:bg-primary transition-colors min-h-[2px]"
                style={{ height: `${(d.logins / maxDay) * 100}%` }}
                title={`${d.date}: ${d.logins}`}
              />
              <div className="absolute -top-5 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-foreground bg-popover border border-border rounded px-1 py-0.5 whitespace-nowrap z-10">
                {d.logins} · {d.date.slice(5)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="top-users-card">
        <div className="px-4 py-3 border-b border-border/40 flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Most active users</h3>
          <span className="text-xs text-muted-foreground">by time on system</span>
        </div>
        {(data.top_users || []).length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No activity in this window.</div>
        ) : (
          <div className="divide-y divide-border/40">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground/70 font-semibold bg-muted/20">
              <div className="col-span-5">User</div>
              <div className="col-span-2">Logins</div>
              <div className="col-span-3">Time on system</div>
              <div className="col-span-2 text-right">Last seen</div>
            </div>
            {data.top_users.map((u, i) => (
              <button key={u.user_id || i} onClick={() => onPick(u.user_id)} className="w-full grid grid-cols-12 gap-3 px-4 py-2.5 items-center text-sm hover:bg-muted/20 text-left" data-testid={`top-user-row-${i}`}>
                <div className="col-span-5 min-w-0 flex items-center gap-2">
                  <UserCircle2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="truncate text-foreground">{u.user_name || u.user_email}</span>
                  <RoleBadge role={u.role} />
                </div>
                <div className="col-span-2 text-muted-foreground">{u.logins}</div>
                <div className="col-span-3 text-foreground">{u.total_label}</div>
                <div className="col-span-2 text-right text-muted-foreground text-xs">{timeAgo(u.last_seen_at)}</div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Sessions tab ─────────────────────────────────────────────────────────
function SessionsTab({ days, onPick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.getActivitySessions({ days, limit: 200 }).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading session history…</div>;
  }
  const sessions = data?.sessions || [];

  return (
    <Card className="p-0 overflow-hidden" data-testid="activity-sessions-tab">
      {sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2" data-testid="activity-sessions-empty">
          <History className="w-8 h-8 opacity-40" />
          <p className="text-sm">No sessions recorded in this window.</p>
        </div>
      ) : (
        <div className="divide-y divide-border/40">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-muted-foreground/70 font-semibold bg-muted/30">
            <div className="col-span-3">User</div>
            <div className="col-span-2">Login</div>
            <div className="col-span-2">Logout</div>
            <div className="col-span-2">Duration</div>
            <div className="col-span-2">Method / IP</div>
            <div className="col-span-1 text-right"></div>
          </div>
          {sessions.map((s, i) => (
            <div key={s.activity_id || i} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm hover:bg-muted/20" data-testid={`session-row-${i}`}>
              <div className="col-span-3 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground truncate">{s.user_name || s.user_email}</span>
                  {s.online && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30" data-testid={`session-online-${i}`}>online</Badge>}
                </div>
                <RoleBadge role={s.role} />
              </div>
              <div className="col-span-2 text-muted-foreground text-xs">{fmtDateTime(s.login_at)}</div>
              <div className="col-span-2 text-muted-foreground text-xs">
                {s.logout_at ? fmtDateTime(s.logout_at) : <span className="italic opacity-60">active</span>}
              </div>
              <div className="col-span-2 text-foreground">{s.duration_label}</div>
              <div className="col-span-2 min-w-0">
                <div className="flex items-center gap-1 text-xs text-muted-foreground capitalize"><Globe className="w-3 h-3" /> {s.login_method || '—'}</div>
                <div className="text-[10px] text-muted-foreground/70 truncate">{s.ip || ''}</div>
              </div>
              <div className="col-span-1 text-right">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-lg" onClick={() => onPick(s.user_id)} data-testid={`session-view-activity-${i}`}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function UserActivityPage() {
  const [tab, setTab] = useState('live');
  const [days, setDays] = useState('30');
  const [pickedUser, setPickedUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="user-activity-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">User Activity</h1>
            <p className="text-sm text-muted-foreground">Who's online, login history, and activity statistics across your workspace.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40 h-9 rounded-lg text-sm" data-testid="activity-window-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`activity-window-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)} className="rounded-lg h-9 gap-1.5" data-testid="activity-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="activity-tabs">
          <TabsTrigger value="live" data-testid="activity-tab-live"><Radio className="w-3.5 h-3.5 mr-1.5" /> Live</TabsTrigger>
          <TabsTrigger value="stats" data-testid="activity-tab-stats"><Activity className="w-3.5 h-3.5 mr-1.5" /> Statistics</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="activity-tab-sessions"><History className="w-3.5 h-3.5 mr-1.5" /> Sessions</TabsTrigger>
        </TabsList>
        <TabsContent value="live" className="mt-4">
          <LiveTab key={`live-${refreshKey}`} onPick={setPickedUser} />
        </TabsContent>
        <TabsContent value="stats" className="mt-4">
          <StatsTab key={`stats-${refreshKey}-${days}`} days={days} onPick={setPickedUser} />
        </TabsContent>
        <TabsContent value="sessions" className="mt-4">
          <SessionsTab key={`sessions-${refreshKey}-${days}`} days={days} onPick={setPickedUser} />
        </TabsContent>
      </Tabs>

      <TimelineDialog userId={pickedUser} onClose={() => setPickedUser(null)} />
    </div>
  );
}
